import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getGeneralReport,
  getGeneralReportGoals,
  saveGeneralReportGoals,
} from '../services/paymentsService.js';

// Dental-Intelligence-style General Report (Daily / Weekly / Monthly).
// Every figure is computed server-side from the Kolla read-model — see
// payments/backend/general_reports.py for definitions.

const PERIODS = [
  { key: 'daily', label: 'Daily' },
  { key: 'weekly', label: 'Weekly' },
  { key: 'monthly', label: 'Monthly' },
];

// Goal fields, in the order they appear on the page. `kind` drives
// formatting and the above/below-goal wording. `basis` limits a field to the
// gross or net goals dialog; fields without one show in both. The net
// production goal keeps its original `production` key.
const GOAL_FIELDS = [
  { key: 'production', label: 'Net production', kind: 'money', basis: 'net' },
  { key: 'grossProduction', label: 'Gross production', kind: 'money', basis: 'gross' },
  { key: 'collections', label: 'Collections', kind: 'money' },
  { key: 'newPatients', label: 'New patients', kind: 'count' },
  { key: 'dentistNetProduction', label: "Dentists' net production", kind: 'money', basis: 'net' },
  { key: 'dentistProductionPerVisit', label: 'Dentist net production per visit', kind: 'money', basis: 'net' },
  { key: 'caseAcceptancePct', label: 'Treatment case acceptance %', kind: 'pct' },
  { key: 'hygieneNetProduction', label: "Hygienists' net production", kind: 'money', basis: 'net' },
  { key: 'hygieneProductionPerVisit', label: 'Hygiene net production per visit', kind: 'money', basis: 'net' },
  { key: 'reappointmentPct', label: 'Re-appointment %', kind: 'pct' },
  { key: 'patientsRescheduled', label: 'Unscheduled patients rescheduled', kind: 'count' },
];

const PERIOD_STORAGE_KEY = 'pm.generalReports.period';
const BASIS_STORAGE_KEY = 'pm.generalReports.productionBasis';

// Gross = procedures posted. Net = gross plus write-offs and other
// adjustments, the way Dentrix reports it.
const BASES = [
  { key: 'gross', label: 'Gross' },
  { key: 'net', label: 'Net' },
];

const readPref = (key, fallback, allowed) => {
  try {
    const v = window.localStorage.getItem(key);
    return allowed.includes(v) ? v : fallback;
  } catch {
    return fallback;
  }
};
const writePref = (key, value) => {
  try { window.localStorage.setItem(key, value); } catch { /* private mode */ }
};

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

const money = (n, { cents = false } = {}) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  return Number(n).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: cents ? 2 : 0,
    maximumFractionDigits: cents ? 2 : 0,
  });
};
const count = (n) => (n === null || n === undefined ? '—' : Number(n).toLocaleString('en-US'));
const pct = (n) => (n === null || n === undefined ? '—' : `${Number(n).toLocaleString('en-US', { maximumFractionDigits: 1 })}%`);
const fmt = (kind, n) => (kind === 'money' ? money(n) : kind === 'pct' ? pct(n) : count(n));

const parseDay = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '');
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};
const toIso = (d) => {
  const p = (x) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
const shortDay = (iso) => parseDay(iso)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) || iso;

const windowTitle = (report) => {
  if (!report) return '';
  const start = parseDay(report.start);
  const fullEnd = parseDay(report.fullEnd);
  if (!start) return '';
  if (report.period === 'daily') {
    return start.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  }
  if (report.period === 'weekly') {
    const range = `Week of ${shortDay(report.start)} – ${fullEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    return report.toDate ? `${range} (to date)` : range;
  }
  const month = start.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  return report.toDate ? `${month} (to date)` : month;
};

const relativeTime = (iso) => {
  if (!iso) return null;
  const t = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`).getTime();
  if (Number.isNaN(t)) return null;
  const mins = Math.max(0, Math.round((Date.now() - t) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 48) return `${hrs} hr ago`;
  return `${Math.round(hrs / 24)} days ago`;
};

// Step the anchor date by one period.
const stepAnchor = (iso, period, dir) => {
  const d = parseDay(iso);
  if (!d) return iso;
  if (period === 'daily') d.setDate(d.getDate() + dir);
  else if (period === 'weekly') d.setDate(d.getDate() + 7 * dir);
  else {
    d.setDate(1);
    d.setMonth(d.getMonth() + dir);
  }
  return toIso(d);
};

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------

// "$178 above goal" / "$2,431 below goal" under a figure.
const GoalStatus = ({ value, goal, kind }) => {
  if (goal === null || goal === undefined || value === null || value === undefined) return null;
  const diff = Number(value) - Number(goal);
  const above = diff >= 0;
  const amount = kind === 'pct'
    ? `${Math.abs(diff).toLocaleString('en-US', { maximumFractionDigits: 1 })} pts`
    : fmt(kind, Math.abs(diff));
  // "85% of goal" — progress toward a money/count goal ("the percentage of
  // where we're at"). Not for pct metrics, where the gap is already in points.
  const ofGoal = kind !== 'pct' && Number(goal) > 0
    ? `${Math.round((Number(value) / Number(goal)) * 100)}% of goal · `
    : '';
  return (
    <div className={`pm-gr-goal-status ${above ? 'is-above' : 'is-below'}`}>
      {diff === 0 ? 'At goal' : `${ofGoal}${amount} ${above ? 'above' : 'below'} goal`}
    </div>
  );
};

// "▼ $6,303 (31%) from Previous WTD"
const Delta = ({ delta, kind, compare }) => {
  if (!delta) return <span className="pm-gr-delta is-flat">—</span>;
  const change = Number(delta.change || 0);
  const dir = change > 0 ? 'up' : change < 0 ? 'down' : 'flat';
  const tooltip = compare
    ? `${shortDay(compare.current.start)}–${shortDay(compare.current.end)}: ${fmt(kind, Number(delta.previous) + change)} vs `
      + `${shortDay(compare.previous.start)}–${shortDay(compare.previous.end)}: ${fmt(kind, delta.previous)}`
    : undefined;
  return (
    <span className={`pm-gr-delta is-${dir}`} title={tooltip}>
      <span aria-hidden="true" className="pm-gr-delta-icon">{dir === 'up' ? '▲' : dir === 'down' ? '▼' : '■'}</span>
      {fmt(kind, Math.abs(change))}
      {delta.pct !== null && delta.pct !== undefined && ` (${Math.abs(Math.round(delta.pct))}%)`}
      <span className="pm-gr-delta-label"> from {compare?.label || 'previous period'}</span>
    </span>
  );
};

const Stat = ({ label, value, emphasis }) => (
  <div className="pm-gr-stat">
    <div className="pm-gr-stat-label">{label}</div>
    <div className={`pm-gr-stat-value${emphasis ? ' is-emphasis' : ''}`}>{value}</div>
  </div>
);

// One report row: title + big figure (+ goal status) on the left, then the
// goal, any breakdown stats, and the previous-period comparison.
const MetricRow = ({ title, value, kind, goal, delta, compare, stats = [], hint, footnote }) => (
  <div className="pm-gr-row">
    <div className="pm-gr-row-main">
      <div className="pm-gr-row-title" title={hint}>{title}</div>
      <div className="pm-gr-row-value">{fmt(kind, value)}</div>
      <GoalStatus value={value} goal={goal} kind={kind} />
      {footnote && <div className="pm-gr-row-footnote">{footnote}</div>}
    </div>
    <div className="pm-gr-row-detail">
      <Stat label="Goal" value={goal === null || goal === undefined ? '—' : fmt(kind, goal)} emphasis={goal !== null && goal !== undefined} />
      {stats.map((s) => <Stat key={s.label} label={s.label} value={s.value} />)}
    </div>
    <div className="pm-gr-row-compare">
      {delta !== undefined && <Delta delta={delta} kind={kind} compare={compare} />}
    </div>
  </div>
);

const Section = ({ tone, title, children }) => (
  <section className={`pm-card pm-gr-section is-${tone}`}>
    <h3 className="pm-gr-section-title">{title}</h3>
    <div>{children}</div>
  </section>
);

// ---------------------------------------------------------------------------
// Goals editor
// ---------------------------------------------------------------------------

const GoalsDialog = ({ practiceId, period, basis, onClose, onSaved }) => {
  const fields = useMemo(() => GOAL_FIELDS.filter((f) => !f.basis || f.basis === basis), [basis]);
  const [allGoals, setAllGoals] = useState(null);
  const [draft, setDraft] = useState({});
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getGeneralReportGoals(practiceId)
      .then((res) => {
        if (cancelled) return;
        const goals = res?.goals || {};
        setAllGoals(goals);
        const current = goals[period] || {};
        setDraft(Object.fromEntries(fields.map((f) => [f.key, current[f.key] ?? ''])));
      })
      .catch(() => !cancelled && setError('Could not load goals.'));
    return () => { cancelled = true; };
  }, [practiceId, period, fields]);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const save = async () => {
    // Start from the period's saved goals so the other basis's goals (not
    // shown in this dialog) are kept; the shown fields are replaced.
    const cleaned = { ...(allGoals[period] || {}) };
    for (const f of fields) delete cleaned[f.key];
    for (const f of fields) {
      const raw = String(draft[f.key] ?? '').trim();
      if (raw === '') continue;
      const n = Number(raw);
      if (!Number.isFinite(n) || n < 0) {
        setError(`${f.label} must be a positive number.`);
        return;
      }
      cleaned[f.key] = n;
    }
    setSaving(true);
    setError(null);
    try {
      await saveGeneralReportGoals(practiceId, { ...allGoals, [period]: cleaned });
      onSaved();
    } catch {
      setError('Saving goals failed. Try again.');
      setSaving(false);
    }
  };

  const periodLabel = PERIODS.find((p) => p.key === period)?.label || period;
  const title = `${periodLabel} ${basis} goals`;
  return (
    <div className="pm-gr-dialog-backdrop" onClick={onClose}>
      <div className="pm-gr-dialog" role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <h3 className="pm-gr-dialog-title">{title}</h3>
        <p className="pm-gr-dialog-sub">
          Targets for one {period === 'daily' ? 'day' : period === 'weekly' ? 'week' : 'month'}. Leave a field blank to hide its goal.
        </p>
        {!allGoals && !error && <div className="pm-gr-muted">Loading…</div>}
        {allGoals && (
          <div className="pm-gr-goal-grid">
            {fields.map((f) => (
              <label key={f.key} className="pm-gr-goal-field">
                <span>{f.label}</span>
                <span className="pm-gr-goal-input">
                  {f.kind === 'money' && <span className="pm-gr-goal-affix">$</span>}
                  <input
                    type="number"
                    min="0"
                    step={f.kind === 'pct' ? '0.1' : '1'}
                    inputMode="decimal"
                    value={draft[f.key]}
                    onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                  />
                  {f.kind === 'pct' && <span className="pm-gr-goal-affix">%</span>}
                </span>
              </label>
            ))}
          </div>
        )}
        {error && <div className="pm-gr-error">{error}</div>}
        <div className="pm-gr-dialog-actions">
          <button type="button" className="pm-btn" onClick={onClose}>Cancel</button>
          <button type="button" className="pm-btn-primary" onClick={save} disabled={!allGoals || saving}>
            {saving ? 'Saving…' : 'Save goals'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tab
// ---------------------------------------------------------------------------

const GeneralReportsTab = ({ practiceId }) => {
  const [period, setPeriod] = useState(() => readPref(PERIOD_STORAGE_KEY, 'daily', ['daily', 'weekly', 'monthly']));
  const [basis, setBasis] = useState(() => readPref(BASIS_STORAGE_KEY, 'net', ['gross', 'net']));
  const chooseBasis = (key) => {
    setBasis(key);
    writePref(BASIS_STORAGE_KEY, key);
  };
  const isNet = basis === 'net';
  const [anchor, setAnchor] = useState(null); // null = today (practice timezone, resolved server-side)
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [goalsOpen, setGoalsOpen] = useState(false);

  const load = useCallback(async (refresh = false) => {
    if (refresh) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const res = await getGeneralReport(practiceId, { period, date: anchor, refresh });
      if (!res?.success) throw new Error(res?.error || 'Report failed');
      setReport(res.report);
    } catch (e) {
      setError('Could not load the report. Try again in a moment.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [practiceId, period, anchor]);

  useEffect(() => { load(false); }, [load]);

  const choosePeriod = (key) => {
    setPeriod(key);
    writePref(PERIOD_STORAGE_KEY, key);
  };

  const today = report?.today;
  const current = anchor || report?.anchor || today;
  const atLatest = !!report && report.end >= today;

  const goals = report?.goals || {};
  const compare = report?.compare;
  const cmp = report?.comparisons || {};

  const header = report?.header;
  const productionValue = header ? (isNet ? header.netProduction : header.grossProduction) : null;
  // Provider rows follow the toggle: ordered by the chosen figure.
  const providerRows = useMemo(() => {
    const rows = [...(report?.productionByProvider || [])];
    const field = isNet ? 'netProduction' : 'grossProduction';
    return rows.sort((a, b) => (b[field] - a[field]) || String(a.name).localeCompare(String(b.name)));
  }, [report, isNet]);

  // Treatment plans sync in the same 5-minute Kolla job as the ledger, so one
  // timestamp covers both.
  const dataAsOf = useMemo(() => {
    const ledger = relativeTime(report?.dataAsOf?.ledger);
    return ledger ? `Last synced ${ledger}` : null;
  }, [report]);

  if (loading && !report) {
    return <div className="pm-gr-muted" style={{ padding: 40 }}>Loading report…</div>;
  }

  const dentists = report?.dentists;
  const hyg = report?.hygienists;
  const front = report?.frontOffice;
  const acceptance = dentists?.caseAcceptance;
  const reappt = hyg?.reappointment;
  const notResched = front?.notRescheduled;
  const resched = front?.rescheduled;
  const unsched = front?.unscheduledActive;

  return (
    <div className="pm-gr">
      {/* Toolbar */}
      <div className="pm-card">
        <div className="pm-toolbar">
          <div className="pm-segmented" role="group" aria-label="Report period">
            {PERIODS.map((p) => (
              <button key={p.key} type="button" className="pm-segment" aria-pressed={period === p.key} onClick={() => choosePeriod(p.key)}>
                {p.label}
              </button>
            ))}
          </div>
          <div className="pm-segmented" role="group" aria-label="Production basis">
            {BASES.map((b) => (
              <button key={b.key} type="button" className="pm-segment" aria-pressed={basis === b.key} onClick={() => chooseBasis(b.key)}>
                {b.label}
              </button>
            ))}
          </div>
          <div className="pm-gr-datenav">
            <button type="button" className="pm-icon-btn" aria-label="Previous period" onClick={() => current && setAnchor(stepAnchor(current, period, -1))}>‹</button>
            <input
              type="date"
              className="pm-gr-date"
              value={current || ''}
              max={today || undefined}
              onChange={(e) => e.target.value && setAnchor(e.target.value)}
              aria-label="Report date"
            />
            <button type="button" className="pm-icon-btn" aria-label="Next period" disabled={atLatest} onClick={() => current && setAnchor(stepAnchor(current, period, 1))}>›</button>
            {anchor && !atLatest && (
              <button type="button" className="pm-btn pm-btn-sm" onClick={() => setAnchor(null)}>Today</button>
            )}
          </div>
          <div className="pm-toolbar-end" style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="pm-btn" onClick={() => setGoalsOpen(true)}>
              {isNet ? 'Set net goals' : 'Set gross goals'}
            </button>
            <button type="button" className="pm-btn" onClick={() => load(true)} disabled={refreshing}>
              {refreshing ? 'Refreshing…' : '↻ Refresh'}
            </button>
          </div>
        </div>
      </div>

      {error && <div className="pm-gr-error" style={{ marginBottom: 16 }}>{error} <button type="button" className="pm-btn pm-btn-sm" onClick={() => load(false)}>Retry</button></div>}

      {report && (
        <>
          <div className="pm-gr-titlebar">
            <h2 className="pm-gr-title">{windowTitle(report)}</h2>
            <div className="pm-gr-meta">{loading ? 'Updating…' : dataAsOf}</div>
          </div>

          {/* Header KPIs */}
          <div className="pm-kpis pm-gr-kpis">
            <div className="pm-kpi">
              <div
                className="pm-kpi-label"
                title={isNet
                  ? 'Procedures posted plus write-offs and other adjustments (courtesies, refunds)'
                  : 'Procedures posted, before write-offs and adjustments'}
              >
                {isNet ? 'Net production' : 'Gross production'}
              </div>
              <div className="pm-kpi-value">
                {money(productionValue)}
                <span className="pm-gr-kpi-aside" title="Scheduled production on the period's appointments">
                  {' / '}{money(header.scheduledProduction)} sched.
                </span>
              </div>
              {isNet && header.writeOffs !== undefined && (
                <div className="pm-kpi-sub">
                  Gross {money(header.grossProduction)} · Write-offs {money(header.writeOffs)} · Adjustments {money(header.adjustments)}
                </div>
              )}
              <GoalStatus value={productionValue} goal={isNet ? goals.production : goals.grossProduction} kind="money" />
              <Delta delta={isNet ? cmp.netProduction : cmp.grossProduction} kind="money" compare={compare} />
            </div>
            <div className="pm-kpi">
              <div className="pm-kpi-label">Collections</div>
              <div className="pm-kpi-value">{money(header.collections)}</div>
              <div className="pm-kpi-sub">Insurance {money(header.insuranceCollections)} · Patient {money(header.patientCollections)}</div>
              {Number(header.netProduction) > 0 && (
                <div
                  className="pm-kpi-sub"
                  title="Collections ÷ net production for this period. Most collections pay for earlier visits, so a single day swings widely; weekly and monthly are the meaningful views."
                >
                  Collection rate {pct((Number(header.collections) / Number(header.netProduction)) * 100)}
                </div>
              )}
              <GoalStatus value={header.collections} goal={goals.collections} kind="money" />
              <Delta delta={cmp.collections} kind="money" compare={compare} />
            </div>
            <div className="pm-kpi">
              <div className="pm-kpi-label">New patients</div>
              <div className="pm-kpi-value">{count(header.newPatients)}</div>
              <GoalStatus value={header.newPatients} goal={goals.newPatients} kind="count" />
              <Delta delta={cmp.newPatients} kind="count" compare={compare} />
            </div>
          </div>

          <Section tone="dentists" title="Dentists">
            <MetricRow
              title="Dentists' Net Production"
              value={dentists.netProduction}
              kind="money"
              goal={goals.dentistNetProduction}
              delta={cmp.dentistNetProduction}
              compare={compare}
              hint="Procedures plus write-offs and adjustments posted by dentists (providers not marked secondary in Dentrix)."
            />
            <MetricRow
              title="Net Production per Visit"
              value={dentists.netProductionPerVisit}
              kind="money"
              goal={goals.dentistProductionPerVisit}
              delta={cmp.dentistProductionPerVisit}
              compare={compare}
              stats={[{ label: 'Visits', value: count(dentists.visits) }]}
              hint="A visit is a patient-day with at least one procedure posted by a dentist — doctor exams during hygiene appointments count."
            />
            <MetricRow
              title="Treatment Case Acceptance % Dollars"
              value={acceptance ? acceptance.pct : null}
              kind="pct"
              goal={goals.caseAcceptancePct}
              stats={acceptance ? [
                { label: 'Presented', value: money(acceptance.presented) },
                { label: 'Accepted', value: money(acceptance.accepted) },
                { label: 'Unscheduled', value: money(acceptance.unscheduled) },
              ] : []}
              footnote={acceptance
                ? `${count(acceptance.plans)} plan${acceptance.plans === 1 ? '' : 's'} presented · accepted = since scheduled (${money(acceptance.scheduled)}) or completed (${money(acceptance.completed)})`
                : 'Treatment plans are still syncing for this practice.'}
              hint="Fees on treatment plans created in the period; accepted when the procedure is on an appointment or completed."
            />
          </Section>

          <Section tone="hygiene" title="Hygienists">
            <MetricRow
              title="Hygienists' Net Production"
              value={hyg.netProduction}
              kind="money"
              goal={goals.hygieneNetProduction}
              delta={cmp.hygieneNetProduction}
              compare={compare}
              hint="Procedures plus write-offs and adjustments posted by Dentrix secondary providers (hygiene / staff)."
            />
            <MetricRow
              title="Net Production per Visit"
              value={hyg.netProductionPerVisit}
              kind="money"
              goal={goals.hygieneProductionPerVisit}
              delta={cmp.hygieneProductionPerVisit}
              compare={compare}
              stats={[{ label: 'Visits', value: count(hyg.visits) }]}
            />
            <MetricRow
              title="Re-Appointment %"
              value={reappt.pct}
              kind="pct"
              goal={goals.reappointmentPct}
              stats={[
                { label: 'Patients', value: count(reappt.patients) },
                { label: 'Re-Appointed', value: count(reappt.reappointed) },
                { label: 'Unscheduled', value: count(reappt.unscheduled) },
              ]}
              hint="Patients with a completed hygiene appointment who had a later appointment booked by the end of the period."
            />
          </Section>

          <Section tone="providers" title="Production by Provider">
            {providerRows.length === 0 ? (
              <div className="pm-gr-muted" style={{ padding: '14px 18px' }}>No production posted in this period.</div>
            ) : (
              <div className="pm-table-scroll">
                <div
                  className="pm-table"
                  style={isNet
                    ? { '--pm-cols': 'minmax(180px, 2fr) 130px repeat(7, minmax(90px, 1fr))', minWidth: 1040 }
                    : { '--pm-cols': 'minmax(180px, 2fr) 130px repeat(4, minmax(90px, 1fr))', minWidth: 720 }}
                >
                  <div className="pm-row pm-row-head">
                    <div>Provider</div>
                    <div>Role</div>
                    <div className="pm-num">Gross production</div>
                    {isNet && (
                      <>
                        <div className="pm-num" title="Adjustments whose type is a write-off (e.g. insurance / UCR write-off)">Write-offs</div>
                        <div className="pm-num" title="Every other adjustment: courtesies, refunds, transfers">Adjustments</div>
                        <div className="pm-num">Net production</div>
                      </>
                    )}
                    <div className="pm-num">Share</div>
                    <div className="pm-num">Visits</div>
                    <div className="pm-num">Per visit</div>
                  </div>
                  {providerRows.map((p) => {
                    const value = isNet ? p.netProduction : p.grossProduction;
                    const perVisit = isNet ? p.netProductionPerVisit : p.grossProductionPerVisit;
                    return (
                      <div key={p.remoteId} className="pm-row">
                        <div className="pm-cell">
                          <div className="pm-cell-title" data-phi>{p.name}</div>
                          {p.name !== p.remoteId && <div className="pm-cell-sub" data-phi>{p.remoteId}</div>}
                        </div>
                        <div className="pm-cell">
                          <span className={`pm-tag ${p.role === 'hygiene' ? 'pm-tag-violet' : p.role === 'dentist' ? 'pm-tag-ok' : ''}`}>
                            {p.role === 'hygiene' ? 'Hygiene / staff' : p.role === 'dentist' ? 'Dentist' : 'Unassigned'}
                          </span>
                        </div>
                        <div className={`pm-cell pm-num ${isNet ? 'pm-cell-text' : 'pm-amount'}`}>{money(p.grossProduction)}</div>
                        {isNet && (
                          <>
                            <div className="pm-cell pm-num pm-cell-text">{money(p.writeOffs)}</div>
                            <div className="pm-cell pm-num pm-cell-text">{money(p.adjustments)}</div>
                            <div className="pm-cell pm-num pm-amount">{money(p.netProduction)}</div>
                          </>
                        )}
                        <div className="pm-cell pm-num pm-cell-text">
                          {productionValue ? pct((value / productionValue) * 100) : '—'}
                        </div>
                        <div className="pm-cell pm-num pm-cell-text">{count(p.visits)}</div>
                        <div className="pm-cell pm-num pm-cell-text">{money(perVisit)}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </Section>

          <Section tone="front" title="Front Office">
            <MetricRow
              title="Unscheduled Patients Rescheduled"
              value={resched.patients}
              kind="count"
              goal={goals.patientsRescheduled}
              stats={[{ label: 'Scheduled Production', value: money(resched.scheduledProduction) }]}
              hint="Patients who booked a new appointment in this period after cancelling or breaking one in the prior 180 days."
            />
            <MetricRow
              title="Patients Not Rescheduled"
              value={notResched.notRescheduled}
              kind="count"
              stats={[
                { label: 'Appt. Value', value: money(notResched.apptValue) },
                { label: 'Existing Pts', value: count(notResched.existingPatients) },
                { label: 'New Pts', value: count(notResched.newPatients) },
                { label: 'Broken Appts', value: count(notResched.brokenAppts) },
              ]}
              footnote={`${count(notResched.cancelledOrBrokenAppts)} cancelled or broken appointment${notResched.cancelledOrBrokenAppts === 1 ? '' : 's'} in the period`}
              hint="Patients whose appointment in this period was cancelled or broken and who have nothing booked since."
            />
            <MetricRow
              title="Unscheduled Active Patients"
              value={unsched.total}
              kind="count"
              stats={[
                { label: 'Est. Value', value: money(unsched.estValue) },
                ...Object.entries(unsched.bucketLabels || {}).map(([key, label]) => ({ label, value: count(unsched.buckets[key]) })),
              ]}
              footnote={`As of ${shortDay(report.end)} · ${count(unsched.notSeenIn18Months)} more active patients not seen in 18+ months`}
              hint={`Active patients with no future appointment, by months since their last visit. Est. value = count × ${money(unsched.avgVisitValue)} average net production per completed visit (last 12 months).`}
            />
          </Section>

          {report.unassigned && report.unassigned.netProduction !== 0 && (
            <div className="pm-gr-note">
              {money(report.unassigned.netProduction)} of production was posted by providers we couldn't place as dentist or hygiene
              {Object.keys(report.unassigned.providers || {}).length > 0 && (
                <> (<span data-phi>{Object.keys(report.unassigned.providers).join(', ')}</span>)</>
              )}.
              It is included in the header totals only.
            </div>
          )}

          <details className="pm-gr-details">
            <summary>Provider roles</summary>
            <p className="pm-gr-muted">From Dentrix: providers marked <em>secondary</em> count as hygiene, everyone else as dentist.</p>
            <ul className="pm-gr-provider-list">
              {(report.providers || []).map((p) => (
                <li key={p.remoteId}>
                  <span data-phi>{p.name} <span className="pm-gr-muted">({p.remoteId})</span></span>
                  <span className={`pm-tag ${p.role === 'hygiene' ? 'pm-tag-violet' : 'pm-tag-ok'}`}>{p.role === 'hygiene' ? 'Hygiene / staff' : 'Dentist'}</span>
                </li>
              ))}
            </ul>
          </details>
        </>
      )}

      {goalsOpen && (
        <GoalsDialog
          practiceId={practiceId}
          period={period}
          basis={basis}
          onClose={() => setGoalsOpen(false)}
          onSaved={() => { setGoalsOpen(false); load(true); }}
        />
      )}
    </div>
  );
};

export default GeneralReportsTab;
